import React, { useState, useMemo, useRef, useEffect } from "react";
import Select from "react-select";
import Button from "../common/Button";
import Icon from "../common/Icon";
import {
  validateLaunchStudyRequest,
  validateDomain,
  getUniqueKeywordEntryCount,
  buildKeywordDedupKey,
} from "../../utils/validators";
import {
  parseBulkKeywords,
  parseCategorizedKeywords,
  parseGeolocatedKeywords,
  getDistinctLocations,
} from "../../utils/keywordParsing";
import { locationsAPI, studiesAPI } from "../../services/api";
import { geocodeInBatches, groupKeywordsByLocation } from "../../utils/geocoding";
import ConfirmDuplicatesModal from "./ConfirmDuplicatesModal";
import ConfirmGeocodingModal from "./ConfirmGeocodingModal";
import "./NewStudyModal.css";

const MAX_LOCATIONS_PER_STUDY = 1000;
const GEOCODING_BATCH_SIZE_DEFAULT = 10;
const GEOCODING_BATCH_SIZE_FR = 1000;
const COUNTRY_CODE_FRANCE = "fr";

const NewStudyModal = ({ isOpen, onClose, onSubmit }) => {
  const [domain, setDomain] = useState("");
  const [projectName, setProjectName] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [geoTarget, setGeoTarget] = useState("2250"); // France by default
  const [language, setLanguage] = useState("1002"); // French by default
  const [countryOptions, setCountryOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [mergeSubdomains, setMergeSubdomains] = useState(true); // Merge by default (current behavior)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmDuplicates, setShowConfirmDuplicates] = useState(false);
  const [validationResultCache, setValidationResultCache] = useState(null);
  const [showConfirmGeocoding, setShowConfirmGeocoding] = useState(false);
  const [geocodingResults, setGeocodingResults] = useState([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingCount, setGeocodingCount] = useState(0);
  const [geocodingProgress, setGeocodingProgress] = useState(0);
  const [geocodingKeywordsByLocation, setGeocodingKeywordsByLocation] = useState({});
  const [pendingKeywords, setPendingKeywords] = useState([]);
  const [recodingInputs, setRecodingInputs] = useState([]);
  const geocodeCacheRef = useRef(new Map());
  const [keywordsFocused, setKeywordsFocused] = useState(false);
  const [categoriesFocused, setCategoriesFocused] = useState(false);
  const [sidePanelClosing, setSidePanelClosing] = useState(false);
  
  // Tabs & Categories
  const [activeTab, setActiveTab] = useState("bulk"); // "bulk" | "categories" | "geolocations"
  const [categorizedKeywordsText, setCategorizedKeywordsText] = useState("");
  const [geolocatedKeywordsText, setGeolocatedKeywordsText] = useState("");

  const showSidePanel = keywordsFocused || categoriesFocused || sidePanelClosing;

  const handleKeywordsBlur = () => {
    setKeywordsFocused(false);
    setSidePanelClosing(true);
    setTimeout(() => {
      setSidePanelClosing(false);
    }, 300);
  };

  const handleCategoriesBlur = () => {
    setCategoriesFocused(false);
    setSidePanelClosing(true);
    setTimeout(() => {
      setSidePanelClosing(false);
    }, 300);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    const loadOptions = async () => {
      try {
        const options = await studiesAPI.getOptions();
        if (cancelled) return;
        setCountryOptions(options.countries || []);
        setOptionsError(null);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load study options:", error);
        setOptionsError("Impossible de charger la liste des pays et des langues.");
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const countryCodeByGeoTarget = useMemo(() => {
    const map = {};
    countryOptions.forEach((country) => {
      map[country.geoTargetConstant] = country.countryCode;
    });
    return map;
  }, [countryOptions]);

  const geoSelectOptions = useMemo(
    () => countryOptions.map((country) => ({ value: country.geoTargetConstant, label: country.name })),
    [countryOptions]
  );

  const selectedCountry = useMemo(
    () => countryOptions.find((country) => country.geoTargetConstant === geoTarget) || null,
    [countryOptions, geoTarget]
  );

  const languageSelectOptions = useMemo(
    () => (selectedCountry?.languages || []).map((lang) => ({ value: lang.languageId, label: lang.name })),
    [selectedCountry]
  );

  const selectedGeoOption = geoSelectOptions.find((option) => option.value === geoTarget) || null;
  const selectedLanguageOption = languageSelectOptions.find((option) => option.value === language) || null;

  const handleGeoTargetChange = (option) => {
    const newGeoTarget = option ? option.value : "";
    setGeoTarget(newGeoTarget);
    const country = countryOptions.find((c) => c.geoTargetConstant === newGeoTarget);
    const firstLanguageId = country && country.languages.length > 0 ? country.languages[0].languageId : "";
    setLanguage(firstLanguageId);
  };

  const bulkKeywords = useMemo(() => parseBulkKeywords(keywordsText), [keywordsText]);
  const categorizedKeywords = useMemo(
    () => parseCategorizedKeywords(categorizedKeywordsText),
    [categorizedKeywordsText]
  );
  const geolocatedKeywords = useMemo(
    () => parseGeolocatedKeywords(geolocatedKeywordsText),
    [geolocatedKeywordsText]
  );

  const keywordCount = useMemo(() => {
    return getUniqueKeywordEntryCount(bulkKeywords);
  }, [bulkKeywords]);

  const rawKeywordCount = bulkKeywords.length;
  const hasDuplicates = rawKeywordCount > keywordCount;
  const categorizedKeywordCount = categorizedKeywords.length;

  const geolocatedGeolocCount = useMemo(
    () => getDistinctLocations(geolocatedKeywords).length,
    [geolocatedKeywords]
  );
  const geolocatedKeywordCount = geolocatedKeywords.length;
  const geolocatedCategoryCount = useMemo(() => {
    const set = new Set();
    geolocatedKeywords.forEach((entry) => {
      if (entry.category) set.add(entry.category);
    });
    return set.size;
  }, [geolocatedKeywords]);
  
  // Get unique categories from categorized keywords
  const getUniqueCategories = () => {
    const categories = new Set();
    categorizedKeywords.forEach(item => {
      if (item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories);
  };
  
  const uniqueCategories = getUniqueCategories();
  const categoryCount = uniqueCategories.length;

  // Check if form has any data
  const hasFormData = () => {
    return (
      domain.trim() !== "" ||
      keywordsText.trim() !== "" ||
      categorizedKeywordsText.trim() !== "" ||
      geolocatedKeywordsText.trim() !== "" ||
      geoTarget !== "2250" ||
      language !== "1002" ||
      mergeSubdomains !== true
    );
  };

  const validate = () => {
    const allKeywordEntries = buildKeywordsWithCategories();

    if (activeTab === 'categories') {
      const categoryErrors = validateCategories();
      if (categoryErrors.length > 0) {
        setErrors({ categories: categoryErrors.join(", ") });
        return false;
      }
    }

    const validationResult = validateLaunchStudyRequest(
      domain,
      allKeywordEntries,
      geoTarget,
      language,
      "Mobile"
    );

    if (!validationResult.isValid) {
      const newErrors = {};

      validationResult.errors.forEach((error) => {
        if (error.includes("domaine")) {
          newErrors.domain = error;
        } else if (error.includes("mot-clé") || error.includes("Limite")) {
          newErrors.keywords = error;
        } else {
          newErrors.general = error;
        }
      });

      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return validationResult;
  };
  
  const validateCategories = () => {
    const errors = [];
    
    // Max 50 categories
    if (categoryCount > 50) {
      errors.push("Maximum 50 catégories autorisées");
    }
    
    // Check category names from categorized keywords
    uniqueCategories.forEach((catName) => {
      if (catName.length > 100) {
        errors.push(`Catégorie "${catName.substring(0, 20)}...": nom trop long (max 100 caractères)`);
      }
      
      // Check for valid characters (unicode letters, numbers, spaces, hyphens, underscores)
      const validNameRegex = /^[\p{L}\p{N}\s\-_]+$/u;
      if (!validNameRegex.test(catName)) {
        errors.push(`Catégorie "${catName}": caractères invalides (utilisez seulement lettres, chiffres, espaces, - et _)`);
      }
    });
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validate();
    if (!validationResult || !validationResult.isValid) {
      return;
    }

    if (activeTab === 'geolocations') {
      const missingLocations = findKeywordsWithoutLocation();
      if (missingLocations.length > 0) {
        setErrors({ general: `${missingLocations.length} mot${missingLocations.length > 1 ? 's' : ''}-clé(s) sans géolocalisation dans l'onglet "Par géolocalisation". Ajoute une ville pour chaque ligne ou utilise un autre onglet.` });
        return;
      }
    }

    const keywordsForGeocoding = buildKeywordsWithCategories();
    const distinctLocations = getDistinctLocations(keywordsForGeocoding);

    if (distinctLocations.length > MAX_LOCATIONS_PER_STUDY) {
      setErrors({ general: `Maximum ${MAX_LOCATIONS_PER_STUDY} géolocalisations distinctes par étude (${distinctLocations.length} détectées).` });
      return;
    }

    setPendingKeywords(keywordsForGeocoding);

    if (distinctLocations.length > 0) {
      setValidationResultCache(validationResult);
      setGeocodingKeywordsByLocation(groupKeywordsByLocation(keywordsForGeocoding));
      await runGeocodingAndShowConfirmation(distinctLocations);
      return;
    }

    const hasDuplicateWarnings = validationResult.warnings && validationResult.warnings.length > 0;

    if (hasDuplicateWarnings) {
      setValidationResultCache(validationResult);
      setShowConfirmDuplicates(true);
      return;
    }

    await submitStudy(validationResult, keywordsForGeocoding, []);
  };

  const findKeywordsWithoutLocation = () => {
    const allKeywords = buildKeywordsWithCategories();
    return allKeywords.filter((kw) => !kw.location || kw.location.trim() === "");
  };

  const runGeocodingAndShowConfirmation = async (distinctLocations) => {
    const countryCode = countryCodeByGeoTarget[geoTarget];
    if (!countryCode) {
      setErrors({ general: "Ce ciblage géographique ne supporte pas la géolocalisation par ville." });
      return;
    }

    setIsGeocoding(true);
    setShowConfirmGeocoding(true);
    setGeocodingResults([]);
    setGeocodingCount(distinctLocations.length);
    setGeocodingProgress(0);

    const batchSize = countryCode === COUNTRY_CODE_FRANCE
      ? GEOCODING_BATCH_SIZE_FR
      : GEOCODING_BATCH_SIZE_DEFAULT;

    try {
      await geocodeInBatches({
        countryCode,
        distinctLocations,
        cache: geocodeCacheRef.current,
        batchSize,
        geocodeRequest: requestGeocodeChunk,
        onProgress: handleGeocodingProgress,
      });
    } catch (error) {
      console.error("Geocoding failed:", error);
      setErrors({ general: "Le géocodage a échoué. Réessaie dans quelques secondes." });
      setShowConfirmGeocoding(false);
    } finally {
      setIsGeocoding(false);
    }
  };

  const requestGeocodeChunk = async (countryCode, chunk) => {
    const response = await locationsAPI.geocode(countryCode, chunk);
    return response.results || [];
  };

  const handleGeocodingProgress = (count, orderedResults) => {
    setGeocodingProgress(count);
    setGeocodingResults(orderedResults);
  };

  const handleCancelGeocoding = () => {
    setShowConfirmGeocoding(false);
    setGeocodingResults([]);
  };

  const handleConfirmGeocoding = async () => {
    if (!validationResultCache) return;

    const hasDuplicateWarnings = validationResultCache.warnings && validationResultCache.warnings.length > 0;
    setShowConfirmGeocoding(false);

    if (hasDuplicateWarnings) {
      setShowConfirmDuplicates(true);
      return;
    }

    await submitStudy(validationResultCache, pendingKeywords, geocodingResults);
  };

  const handleRecodeCorrections = async (corrections) => {
    const countryCode = countryCodeByGeoTarget[geoTarget];
    if (!countryCode) return;

    const correctionEntries = Object.entries(corrections);
    if (correctionEntries.length === 0) return;

    const newInputs = correctionEntries.map(([, corrected]) => corrected);
    const originalInputs = correctionEntries.map(([original]) => original);
    const originalLowerKeys = originalInputs.map((original) => original.toLowerCase());
    const originalSetLower = new Set(originalLowerKeys);

    setRecodingInputs(originalInputs);

    const batchSize = countryCode === COUNTRY_CODE_FRANCE
      ? GEOCODING_BATCH_SIZE_FR
      : GEOCODING_BATCH_SIZE_DEFAULT;

    try {
      const newResults = await geocodeInBatches({
        countryCode,
        distinctLocations: newInputs,
        cache: geocodeCacheRef.current,
        batchSize,
        geocodeRequest: requestGeocodeChunk,
        onProgress: null,
      });

      setGeocodingResults((prev) => mergeGeocodingResults(prev, newResults, originalSetLower));
      setPendingKeywords((prev) => remapKeywordLocations(prev, corrections));
      setGeocodingKeywordsByLocation((prev) => remapKeywordsByLocation(prev, corrections));
    } catch (error) {
      console.error("Re-geocoding failed:", error);
      setErrors({ general: "Le re-géocodage a échoué. Réessaie dans quelques secondes." });
    } finally {
      setRecodingInputs([]);
    }
  };

  const submitStudy = async (validationResult, keywordEntries, geocoded) => {
    setIsSubmitting(true);

    const keywordsWithCategories = enrichKeywordsWithGeocoding(keywordEntries, geocoded);

    const trimmedProjectName = projectName.trim();
    const studyData = {
      domain: domain.trim(),
      name: trimmedProjectName.length > 0 ? trimmedProjectName : null,
      keywordsWithCategories: keywordsWithCategories,
      geoTargetConstant: geoTarget,
      languageId: language,
      deviceTarget: "Mobile",
      mergeSubdomains: mergeSubdomains,
    };

    try {
      await onSubmit(studyData);
      handleClose();
    } catch (error) {
      console.error("Error submitting study:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const enrichKeywordsWithGeocoding = (keywordEntries, geocoded) => {
    const byInput = new Map();
    (geocoded || []).forEach((r) => {
      byInput.set(r.input.toLowerCase(), r);
    });

    return keywordEntries.map((entry) => {
      const rawLocation = entry.location;
      if (!rawLocation) {
        return {
          keyword: entry.keyword,
          category: entry.category ?? null,
          formattedAddress: null,
          latitude: null,
          longitude: null,
        };
      }
      const match = byInput.get(rawLocation.toLowerCase());
      return {
        keyword: entry.keyword,
        category: entry.category ?? null,
        formattedAddress: match?.formattedAddress ?? rawLocation,
        latitude: match?.latitude ?? null,
        longitude: match?.longitude ?? null,
      };
    });
  };

  const mergeGeocodingResults = (previous, newResults, originalSetLower) => {
    const byInput = new Map();
    previous.forEach((r) => {
      if (!originalSetLower.has(r.input.toLowerCase())) {
        byInput.set(r.input.toLowerCase(), r);
      }
    });
    newResults.forEach((r) => byInput.set(r.input.toLowerCase(), r));
    return [...byInput.values()];
  };

  const remapKeywordLocations = (keywords, corrections) => {
    const correctionPairs = Object.entries(corrections).map(([original, corrected]) => [original.toLowerCase(), corrected]);
    const correctionByLower = new Map(correctionPairs);
    const remapped = keywords.map((kw) => {
      if (!kw.location) return kw;
      const corrected = correctionByLower.get(kw.location.toLowerCase());
      return corrected ? { ...kw, location: corrected } : kw;
    });
    return dedupeKeywordEntries(remapped);
  };

  const dedupeKeywordEntries = (keywords) => {
    const seen = new Set();
    const deduped = [];
    keywords.forEach((kw) => {
      const key = buildKeywordDedupKey(kw);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(kw);
    });
    return deduped;
  };

  const remapKeywordsByLocation = (byLocation, corrections) => {
    const next = { ...byLocation };
    Object.entries(corrections).forEach(([original, corrected]) => {
      const oldKey = original.toLowerCase();
      const newKey = corrected.toLowerCase();
      const moved = next[oldKey];
      if (!moved) return;
      delete next[oldKey];
      const existing = next[newKey] || [];
      const merged = [...existing, ...moved];
      next[newKey] = [...new Set(merged)];
    });
    return next;
  };

  const handleConfirmDuplicates = async () => {
    setShowConfirmDuplicates(false);
    await submitStudy(validationResultCache, pendingKeywords, geocodingResults);
  };

  const handleCancelDuplicates = () => {
    setShowConfirmDuplicates(false);
    setValidationResultCache(null);
  };

  const handleClose = () => {
    setDomain("");
    setProjectName("");
    setKeywordsText("");
    setCategorizedKeywordsText("");
    setGeolocatedKeywordsText("");
    setGeoTarget("2250");
    setLanguage("1002");
    setMergeSubdomains(true);
    setErrors({});
    setShowConfirmDuplicates(false);
    setValidationResultCache(null);
    setShowConfirmGeocoding(false);
    setGeocodingResults([]);
    setIsGeocoding(false);
    setGeocodingCount(0);
    setGeocodingProgress(0);
    setGeocodingKeywordsByLocation({});
    setPendingKeywords([]);
    setRecodingInputs([]);
    geocodeCacheRef.current.clear();
    setActiveTab("bulk");
    onClose();
  };

  const buildKeywordsWithCategories = () => {
    if (activeTab === 'bulk') {
      return bulkKeywords.map((entry) => ({
        keyword: entry.keyword,
        category: null,
        location: entry.location,
      }));
    }
    if (activeTab === 'geolocations') {
      return geolocatedKeywords;
    }
    return categorizedKeywords;
  };

  const getTotalKeywordCount = () => {
    if (activeTab === 'bulk') return bulkKeywords.length;
    if (activeTab === 'geolocations') return geolocatedKeywords.length;
    return categorizedKeywords.length;
  };
  
  const totalKeywordCount = getTotalKeywordCount();

  const activeKeywordEntries = buildKeywordsWithCategories();
  const activeUniqueKeywordCount = getUniqueKeywordEntryCount(activeKeywordEntries);
  const activeDuplicateCount = activeKeywordEntries.length - activeUniqueKeywordCount;

  const handleOverlayClick = () => {
    // Only close if form is empty
    if (!hasFormData()) {
      handleClose();
    }
    // If form has data, do nothing (don't close)
  };

  const handlePasteKeywords = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setKeywordsText((prevText) => prevText + (prevText ? "\n" : "") + text);
    } catch (err) {
      console.error("Failed to read clipboard:", err);
    }
  };
  
  const handleTabKeyInTextarea = (e, setter) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const newValue = value.substring(0, start) + '\t' + value.substring(end);
    setter(newValue);
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + 1;
    }, 0);
  };

  const handleTabInBulkKeywords = (e) =>
    handleTabKeyInTextarea(e, setKeywordsText);

  const handleTabInCategorizedKeywords = (e) =>
    handleTabKeyInTextarea(e, setCategorizedKeywordsText);

  const handleTabInGeolocatedKeywords = (e) =>
    handleTabKeyInTextarea(e, setGeolocatedKeywordsText);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>
              <Icon name="Plus" size={24} />
              Nouveau Projet
            </h2>
            <button className="modal-close" onClick={handleClose} type="button">
              <Icon name="X" size={24} />
            </button>
          </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Domain Input */}
            <div className="form-group">
              <label htmlFor="domain" className="form-label">
                Domaine à analyser *
              </label>
              <input
                id="domain"
                type="text"
                className={`form-input ${errors.domain ? "error" : ""}`}
                placeholder="exemple: example.com"
                value={domain}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setDomain(newValue);

                  // Real-time validation
                  if (newValue.trim()) {
                    const domainValidation = validateDomain(newValue);
                    if (!domainValidation.isValid) {
                      setErrors((prev) => ({
                        ...prev,
                        domain: domainValidation.error,
                      }));
                    } else {
                      setErrors((prev) => {
                        const { domain, ...rest } = prev;
                        return rest;
                      });
                    }
                  }
                }}
                disabled={isSubmitting}
              />
              {errors.domain && (
                <span className="form-error">
                  <Icon name="AlertCircle" size={14} />
                  {errors.domain}
                </span>
              )}
            </div>

            {/* Project Name */}
            <div className="form-group">
              <label htmlFor="projectName" className="form-label">
                Nom du projet
              </label>
              <input
                id="projectName"
                type="text"
                className="form-input"
                placeholder={domain.trim() ? domain.trim() : "Laissé vide pour utiliser le domaine"}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                maxLength={255}
                disabled={isSubmitting}
              />
            </div>

            {/* Geo Target */}
            <div className="form-group">
              <label htmlFor="geoTarget" className="form-label">
                Ciblage géographique
              </label>
              <Select
                inputId="geoTarget"
                classNamePrefix="rs"
                options={geoSelectOptions}
                value={selectedGeoOption}
                onChange={handleGeoTargetChange}
                isDisabled={isSubmitting}
                placeholder="Rechercher un pays..."
                noOptionsMessage={() => "Aucun pays"}
              />
            </div>

            {/* Language */}
            <div className="form-group">
              <label htmlFor="language" className="form-label">
                Langue
              </label>
              <Select
                inputId="language"
                classNamePrefix="rs"
                options={languageSelectOptions}
                value={selectedLanguageOption}
                onChange={(option) => setLanguage(option ? option.value : "")}
                isDisabled={isSubmitting}
                placeholder="Rechercher une langue..."
                noOptionsMessage={() => "Aucune langue"}
              />
              {optionsError && (
                <span className="form-error">
                  <Icon name="AlertCircle" size={14} />
                  {optionsError}
                </span>
              )}
            </div>

            {/* Subdomain Handling */}
            <div className="form-group">
              <label className="checkbox-label" htmlFor="mergeSubdomains">
                <input
                  type="checkbox"
                  id="mergeSubdomains"
                  checked={!mergeSubdomains}
                  onChange={(e) => setMergeSubdomains(!e.target.checked)}
                  disabled={isSubmitting}
                />
                <span>Séparer les sous-domaines (www, blog, shop, etc.)</span>
              </label>
              <p className="form-help-text">
                Si coché, chaque sous-domaine sera analysé individuellement. Sinon, tous les sous-domaines seront fusionnés sous le domaine racine.
              </p>
            </div>

            {/* Keywords Section */}
            <div className="keywords-section">
              <div className="keywords-section-header">
                <h3 className="keywords-section-title">Mots-clés *</h3>
              </div>

              {/* Tabs Navigation */}
              <div className="tabs-container">
                <div className="tabs-header">
                  <div className="tabs-navigation">
                    <button
                      type="button"
                      className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
                      onClick={() => setActiveTab('bulk')}
                      disabled={isSubmitting}
                    >
                      <Icon name="AlignJustify" size={16} />
                      <span>Saisie libre</span>
                      {bulkKeywords.length > 0 && (
                        <span className="tab-badge">{bulkKeywords.length}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
                      onClick={() => setActiveTab('categories')}
                      disabled={isSubmitting}
                    >
                      <Icon name="Layers" size={16} />
                      <span>Par catégorie</span>
                      {categoryCount > 0 && (
                        <span className="tab-badge">{categoryCount}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`tab-button ${activeTab === 'geolocations' ? 'active' : ''}`}
                      onClick={() => setActiveTab('geolocations')}
                      disabled={isSubmitting}
                    >
                      <Icon name="MapPin" size={16} />
                      <span>Par localisation</span>
                      {geolocatedGeolocCount > 0 && (
                        <span className="tab-badge">{geolocatedGeolocCount}</span>
                      )}
                    </button>
                  </div>
                  
                  {/* Total keyword count indicator */}
                  <div className="total-keywords-indicator">
                    <span className={totalKeywordCount > 1000 ? "error" : totalKeywordCount > 800 ? "warning" : ""}>
                      {totalKeywordCount} / 1000
                    </span>
                  </div>
                </div>
              </div>

            {/* Tab Content: Bulk Keywords */}
            {activeTab === 'bulk' && (
              <div className="form-group bulk-keywords-content">
              <div className="textarea-container">
                <textarea
                  id="keywords"
                  className={`form-textarea ${errors.keywords ? "error" : ""}`}
                  placeholder={"chaussures running\nbaskets sport\nsneakers confort\nrunning shoes\n\nFormat : un mot-clé par ligne (mode national)"}
                  value={keywordsText}
                  onChange={(e) => setKeywordsText(e.target.value)}
                  onKeyDown={handleTabInBulkKeywords}
                  onFocus={() => setKeywordsFocused(true)}
                  onBlur={handleKeywordsBlur}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="paste-button"
                  onClick={handlePasteKeywords}
                  disabled={isSubmitting}
                  title="Coller depuis le presse-papiers"
                >
                  <Icon name="Clipboard" size={16} />
                </button>
              </div>
              <div className="bulk-keywords-footer">
                {keywordCount > 0 && (
                  <span className="keywords-counter-inline">
                    {keywordCount} mot{keywordCount > 1 ? 's-clés' : '-clé'}
                    {hasDuplicates && (
                      <span style={{ fontSize: '11px', opacity: 0.7, marginLeft: '4px' }}>
                        ({rawKeywordCount - keywordCount} doublon{rawKeywordCount - keywordCount > 1 ? 's' : ''})
                      </span>
                    )}
                  </span>
                )}
                <span className="form-hint">
                  <Icon name="Info" size={14} />
                  Une ligne par mot-clé. Mode national (limite : 1000 mots-clés).
                </span>
              </div>
              {errors.keywords && (
                <span className="form-error">
                  <Icon name="AlertCircle" size={14} />
                  {errors.keywords}
                </span>
              )}
            </div>
            )}

            {/* Tab Content: Categories */}
            {activeTab === 'categories' && (
              <div className="form-group categories-tab-content">
                <div className="textarea-container">
                  <textarea
                    id="categorizedKeywords"
                    className={`form-textarea ${errors.categories ? "error" : ""}`}
                    placeholder={"Chaussures   baskets running\nChaussures   sneakers mode\nVêtements    t-shirt coton\nVêtements    pull en laine\n\nFormat : Catégorie [TAB] mot-clé (mode national)"}
                    value={categorizedKeywordsText}
                    onChange={(e) => setCategorizedKeywordsText(e.target.value)}
                    onKeyDown={handleTabInCategorizedKeywords}
                    onFocus={() => setCategoriesFocused(true)}
                    onBlur={handleCategoriesBlur}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="paste-button"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        setCategorizedKeywordsText((prevText) => prevText + (prevText ? "\n" : "") + text);
                      } catch (err) {
                        console.error("Failed to read clipboard:", err);
                      }
                    }}
                    disabled={isSubmitting}
                    title="Coller depuis le presse-papiers"
                  >
                    <Icon name="Clipboard" size={16} />
                  </button>
                </div>
                <div className="bulk-keywords-footer">
                  {categorizedKeywordCount > 0 && (
                    <span className="keywords-counter-inline">
                      {categorizedKeywordCount} mot{categorizedKeywordCount > 1 ? 's-clés' : '-clé'} · {categoryCount} catégorie{categoryCount > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="form-hint">
                    <Icon name="Info" size={14} />
                    Format : Catégorie [TAB] mot-clé. Pas de géolocalisation = mode national.
                  </span>
                </div>
                {errors.categories && (
                  <span className="form-error">
                    <Icon name="AlertCircle" size={14} />
                    {errors.categories}
                  </span>
                )}
              </div>
            )}

            {/* Tab Content: Geolocations */}
            {activeTab === 'geolocations' && (
              <div className="form-group categories-tab-content">
                <div className="textarea-container">
                  <textarea
                    id="geolocatedKeywords"
                    className={`form-textarea ${errors.geolocations ? "error" : ""}`}
                    placeholder={"Location Box   louer un box   Brest\nLocation Box   louer un box   Montpellier\nPlomberie      plombier       Paris\nPlomberie      plombier       Lyon\n\nFormat : Catégorie [TAB] mot-clé [TAB] ville (les trois sont requis)"}
                    value={geolocatedKeywordsText}
                    onChange={(e) => setGeolocatedKeywordsText(e.target.value)}
                    onKeyDown={handleTabInGeolocatedKeywords}
                    onFocus={() => setCategoriesFocused(true)}
                    onBlur={handleCategoriesBlur}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="paste-button"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        setGeolocatedKeywordsText((prevText) => prevText + (prevText ? "\n" : "") + text);
                      } catch (err) {
                        console.error("Failed to read clipboard:", err);
                      }
                    }}
                    disabled={isSubmitting}
                    title="Coller depuis le presse-papiers"
                  >
                    <Icon name="Clipboard" size={16} />
                  </button>
                </div>
                <div className="bulk-keywords-footer">
                  {geolocatedKeywordCount > 0 && (
                    <span className="keywords-counter-inline">
                      {geolocatedKeywordCount} mot{geolocatedKeywordCount > 1 ? 's-clés' : '-clé'} · {geolocatedCategoryCount} catégorie{geolocatedCategoryCount > 1 ? 's' : ''} · {geolocatedGeolocCount} localisation{geolocatedGeolocCount > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="form-hint">
                    <Icon name="Info" size={14} />
                    Format : Catégorie [TAB] mot-clé [TAB] ville (les trois colonnes sont requises)
                  </span>
                </div>
                {errors.geolocations && (
                  <span className="form-error">
                    <Icon name="AlertCircle" size={14} />
                    {errors.geolocations}
                  </span>
                )}
              </div>
            )}

            </div> {/* Close keywords-section */}

            {errors.submit && (
              <div className="form-error-box">
                <Icon name="AlertCircle" size={18} />
                {errors.submit}
              </div>
            )}

            {errors.general && (
              <div className="form-error-box">
                <Icon name="AlertCircle" size={18} />
                {errors.general}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || totalKeywordCount === 0 || !domain.trim()}
              icon={isSubmitting ? null : <Icon name="Check" size={18} />}
            >
              {isSubmitting ? "Création en cours..." : "Créer le projet"}
            </Button>
          </div>
        </form>
        </div>

        {/* Side Panel - Best Practices */}
        {showSidePanel && (
          <div 
            className={`modal-side-panel ${sidePanelClosing ? 'closing' : ''}`}
            aria-hidden={sidePanelClosing}
            role="complementary"
            aria-label="Bonnes pratiques"
          >
            <div className="side-panel-header">
              <Icon name="Lightbulb" size={24} />
              <h3>Bonnes pratiques</h3>
            </div>
            <div className="side-panel-content">
              <p>
                Avant de renseigner les mots-clés de votre étude, il est recommandé d'uniformiser la data 
                <strong> (singulier/pluriel, accent/sans accent, pas de symbole/tiret ...)</strong> afin 
                de maximiser l'analyse de mots-clés et le potentiel par clusters.
              </p>
              
              {activeTab === 'bulk' && (
                <>
                  <p style={{ marginTop: '16px' }}>
                    <strong>Format attendu (mode national) :</strong>
                  </p>
                  <div className="format-example" style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    marginTop: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    <div>un mot-clé par ligne</div>
                  </div>
                  <p style={{ marginTop: '12px', fontSize: '13px', opacity: 0.9 }}>
                    Cet onglet lance le scraping en mode national (sans localisation). Utilisez l'onglet « Par localisation » pour cibler une ville précise.
                  </p>
                </>
              )}

              {activeTab === 'categories' && (
                <>
                  <p style={{ marginTop: '16px' }}>
                    <strong>Format attendu (mode national) :</strong>
                  </p>
                  <div className="format-example" style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    marginTop: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    <div>Catégorie<span style={{ color: 'var(--color-purple-accent)' }}>[TAB]</span>mot-clé</div>
                  </div>
                  <p style={{ marginTop: '12px', fontSize: '13px', opacity: 0.9 }}>
                    <Icon name="Table" size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    <strong>Depuis Google Sheets / Excel :</strong> copiez 2 colonnes (Catégorie | Mot-clé) et collez ici.
                  </p>
                  <p style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
                    Cet onglet lance le scraping en mode national (sans localisation). Utilisez l'onglet « Par localisation » pour cibler une ville précise.
                  </p>
                </>
              )}

              {activeTab === 'geolocations' && (
                <>
                  <p style={{ marginTop: '16px' }}>
                    <strong>Format attendu (mode local) :</strong>
                  </p>
                  <div className="format-example" style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    marginTop: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    <div>Catégorie<span style={{ color: 'var(--color-purple-accent)' }}>[TAB]</span>mot-clé<span style={{ color: 'var(--color-purple-accent)' }}>[TAB]</span>ville</div>
                  </div>
                  <p style={{ marginTop: '12px', fontSize: '13px', opacity: 0.9 }}>
                    <Icon name="Table" size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    <strong>Depuis Google Sheets / Excel :</strong> copiez 3 colonnes (Catégorie | Mot-clé | Ville) et collez ici. Les trois sont requises.
                  </p>
                  <p style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
                    Le même mot-clé peut être répété avec plusieurs villes différentes pour comparer les positions par zone locale.
                  </p>
                </>
              )}
              
              <p style={{ marginTop: '16px' }}>
                <strong>Maximum : 1000 mots-clés</strong> par étude.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmDuplicatesModal
        isOpen={showConfirmDuplicates}
        onConfirm={handleConfirmDuplicates}
        onCancel={handleCancelDuplicates}
        duplicateCount={activeDuplicateCount}
        uniqueCount={activeUniqueKeywordCount}
      />

      <ConfirmGeocodingModal
        isOpen={showConfirmGeocoding}
        results={geocodingResults}
        expectedCountryCode={countryCodeByGeoTarget[geoTarget] || ""}
        onConfirm={handleConfirmGeocoding}
        onCancel={handleCancelGeocoding}
        onRecodeCorrections={handleRecodeCorrections}
        recodingInputs={recodingInputs}
        isLoading={isGeocoding}
        loadingCount={geocodingCount}
        loadingProgress={geocodingProgress}
        keywordsByLocation={geocodingKeywordsByLocation}
      />
    </div>
  );
};

export default NewStudyModal;
